import { HistorySection } from "@/components/history/HistorySection";
import { ListButton } from "@refinedev/antd";
import {
  Col,
  Row,
  Spin,
  Typography,
  message,
  Tag,
  Card,
  Badge,
  Divider,
  CollapseProps,
  Collapse,
  Input,
} from "antd";
import { useNavigation, useTranslate } from "@refinedev/core";
import {
  BankOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FlagOutlined,
  HistoryOutlined,
  LeftOutlined,
  MailOutlined,
  ShopOutlined,
  StarOutlined,
  TeamOutlined,
  TruckOutlined,
  WhatsAppOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Flex, theme } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import {
  axiosInstance,
  billingDisplayName,
  deriveOrderShipmentStatus,
  displayEmail,
  formatPrice,
  nombreDeEntrega,
} from "@/utilities";
import {
  API_BASE_URL,
  LINK_MARKETPLACE_HASH,
  VITE_GOOGLE_MAPS_API_KEY,
} from "@/services/XCONS_ENV";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChannelPickerDropdown, channelIcon, useWritableChannels } from "@/components/channel-picker";
import {
  BoxCloseIcon,
  OrderPayment,
  OrderRating,
  OrderShipment,
  OrderSumary,
  PresupuestoIcon,
  SellerIcon,
} from "@/components";
import mapImagePng from "@/components/icon/images/map.png";
import React from "react";
import dayjs from "dayjs";
import { LateralModal } from "../contacts";
import { DeliveryAddress } from "./deliveryAddress";
import { CubePlay } from "@/components/icon/CubePlay";
import { PermissionsLateral } from "@/components/permissions-lateral";
import { useOperatorContacts, filterOutOperators } from "@/hooks/useOperatorContacts";
import { ShipmentProgress } from "@/utilities/calcule-delivery-porcent";
import { CubePlayCancel } from "@/components/icon/CubePlayCancel";
import { CubePlayFinish } from "@/components/icon/CubePlayFinish";

const { Title, Text, Link } = Typography;

type Participation = {
  position?: number;
  participationType?: "store" | "seller" | string;
  contact?: { user?: { firstname?: string; lastname?: string } };
};

export const OrderDetails: React.FC = () => {
  const [orderLoading, setOrderLoading] = useState<boolean>(true);
  const [orderData, setOrderData] = useState<any>([]);
  const [deliveryNames, setDeliveryNames] = useState<string[]>([]);
  const [mapImage, setMapImage] = useState<string>("");
  const [canEnableDelivery, setCanEnableDelivery] = useState(false);
  // XCONS-1929 bulk cancel pending deliveries: 'off' es el default, 'select'
  // se entra desde el menu 3-puntos del header de Entregas. El estado
  // 'preview' (request hecho) se DERIVA de los shipments del backend
  // (alguno con `metadata.bulkCancelRequest.groupId` + status pending_cancel).
  const [bulkCancelMode, setBulkCancelMode] = useState<"off" | "select">(
    "off",
  );

  const norm = (v: any) =>
    String(v ?? "")
      .trim()
      .toLowerCase();

  const PANEL_KEYS = {
    payments: "2",
    shipments: "3",
  } as const;

  function deriveActiveKeys(orderData: any): string[] {
    const payments = orderData?.payments ?? [];
    const creditMemos = orderData?.creditMemos ?? [];

    const hasPayments = payments.length > 0;
    const allPaymentsInvoiced =
      hasPayments && payments.every((p: any) => norm(p.status) === "invoiced");

    // Si existe alguna NC que NO esté en su estado final (payment_verified)
    // mantener el panel de pagos abierto. Antes el chequeo era `!== approved`,
    // lo que cerraba el panel al transicionar de pending→approved aunque la
    // NC todavía no estuviera cobrada/verificada (pending_payment +
    // payment_verified).
    const hasActiveCreditMemo =
      creditMemos.length > 0 &&
      creditMemos.some((cm: any) => norm(cm.status) !== "payment_verified");

    if (hasActiveCreditMemo) {
      return [PANEL_KEYS.payments, PANEL_KEYS.shipments];
    }

    return allPaymentsInvoiced ? [PANEL_KEYS.shipments] : [PANEL_KEYS.payments];
  }

  const [activeCollapseKey, setActiveCollapseKey] = useState<string[]>(() =>
    deriveActiveKeys(orderData),
  );
  const [historySearch, setHistorySearch] = useState("");
  const [historyQ, setHistoryQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setHistoryQ(historySearch.trim()), 350);
    return () => clearTimeout(t);
  }, [historySearch]);

  /**
   * **Al facturar, primero se ve el pago llegar a Facturado y después se pasa a Entregas.**
   * Si el cambio cierra Pagos en el mismo momento en que llega el estado nuevo, el avance
   * del flujo no llega a verse. La pausa es sólo para ese paso, no al abrir el pedido.
   */
  const ESPERA_ANTES_DE_ENTREGAS_MS = 1500;
  const panelesPrevios = useRef<string[] | null>(null);

  useEffect(() => {
    if (!orderData) return;
    const siguientes = deriveActiveKeys(orderData);
    const antes = panelesPrevios.current;
    panelesPrevios.current = siguientes;

    const pasaAEntregas =
      antes?.includes(PANEL_KEYS.payments) &&
      !siguientes.includes(PANEL_KEYS.payments) &&
      siguientes.includes(PANEL_KEYS.shipments);

    if (!pasaAEntregas) {
      setActiveCollapseKey(siguientes);
      return;
    }
    const timer = setTimeout(
      () => setActiveCollapseKey(siguientes),
      ESPERA_ANTES_DE_ENTREGAS_MS,
    );
    return () => clearTimeout(timer);
  }, [
    orderData?.updatedAt,
    orderData?.payments?.length,
    orderData?.creditMemos?.length,
  ]);

  useEffect(() => {
    const paymentHandler = () => {
      setActiveCollapseKey((prev) =>
        prev.includes(PANEL_KEYS.payments) ? prev : [...prev, PANEL_KEYS.payments],
      );
    };
    const shipmentHandler = () => {
      setActiveCollapseKey((prev) =>
        prev.includes(PANEL_KEYS.shipments) ? prev : [...prev, PANEL_KEYS.shipments],
      );
    };

    window.addEventListener("openPaymentPanel", paymentHandler);
    window.addEventListener("openShipmentPanel", shipmentHandler);
    
    return () => {
      window.removeEventListener("openPaymentPanel", paymentHandler);
      window.removeEventListener("openShipmentPanel", shipmentHandler);
    };
  }, []);

  const hasUnapprovedCreditMemo =
    (orderData?.creditMemos?.length ?? 0) > 0 &&
    orderData.creditMemos.some((cm: any) => norm(cm.status) !== "approved");

  const t = useTranslate();
  const { token } = theme.useToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { goBack, push } = useNavigation();

  const recordId = location.state?.record;
  const { id } = useParams();
  const recordOrderId = recordId || id;

  const handleBack = (reason?: "missing" | "error") => {
    goBack();
  };

  // `silencioso`: refresca sin la ruedita sobre toda la página. Es lo que pasa después de un
  // cambio de estado, para que el flujo muestre el avance en vez de parecer una recarga.
  const getOrder = async (orderId: string, silencioso = false) => {
    if (!silencioso) setOrderLoading(true);
    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/orders/${orderId}`);
      setOrderData(res.data);
    } catch (error: any) {
      console.error(error);
      if (error.message === "Insufficient permissions") {
        message.error(t("insufficient.permissions"));
        push("/orders");
      } else {
        message.error("Error al cargar el template");
        handleBack("error");
      }
    } finally {
      setOrderLoading(false);
    }
  };


  useEffect(() => {
    if (!recordOrderId) {
      handleBack("missing");
    } else {
      getOrder(recordOrderId);
    }
  }, [recordOrderId]);

  const refreshOrder = () => {
    if (recordOrderId) {
      getOrder(recordOrderId, true);
    }
  };


  const asArray = <T,>(v: T | T[] | null | undefined): T[] =>
    Array.isArray(v) ? v : v ? [v] : [];

  const pickOwnerByType = (
    participations: Participation[] | undefined,
    type: "store" | "seller",
  ): Participation | undefined => {
    if (!participations?.length) return undefined;
    const ofType = participations.filter((p) => p.participationType === type);
    if (!ofType.length) return undefined;

    const pos0 = ofType.find((p) => (p.position ?? Infinity) === 0);
    if (pos0) return pos0;
    return ofType
      .slice()
      .sort((a, b) => (a.position ?? 999) - (b.position ?? 999))[0];
  };

  const contacts = asArray(orderData?.contactDetails);

  const contactGroupsNames: string[] = contacts.flatMap((c: any) =>
    (c?.contactGroups ?? []).map((g: any) => g?.name).filter(Boolean),
  );

  const operatorContactIds = useOperatorContacts();

  // Hide the technical operator service-account that Magento attaches to every
  // order made through the storefront (config api/general/customer_email).
  // Filter before deriving owners so header/contact card don't pick it up.
  const sourceParticipations = filterOutOperators<any>(
    asArray<Participation>(orderData?.orderParticipations as any),
    operatorContactIds,
  ) as Participation[];

  const storeOwner = pickOwnerByType(sourceParticipations, "store");
  const sellerOwner = pickOwnerByType(sourceParticipations, "seller");

  // Customer owner = participation with position=0 AND participationType="" or null.
  // Derived live from participations so that changing the owner in the side
  // panel updates the header/contact card immediately (sales_order.customer_*
  // is persisted at place-order time and doesn't follow participation moves).
  // Names/email/whatsapp live under participation.contact.user — the backend
  // hydrates that relation in findAllByEntityAndCodes (orders.service.ts:231).
  const customerOwner = (() => {
    const customers = sourceParticipations.filter(
      (p: any) => (p?.participationType ?? "") === "",
    );
    if (!customers.length) return undefined;
    const pos0 = customers.find((p: any) => (p?.position ?? Infinity) === 0);
    return (
      pos0 ??
      customers
        .slice()
        .sort(
          (a: any, b: any) => (a?.position ?? 999) - (b?.position ?? 999),
        )[0]
    );
  })();
  const ownerUser = (customerOwner as any)?.contact?.user;
  const ownerFullName = ownerUser
    ? `${ownerUser.firstname ?? ""} ${ownerUser.lastname ?? ""}`.trim()
    : "";
  const ownerWhatsapp = ownerUser?.whatsapp;
  const ownerEmail = ownerUser?.email;
  // Con el teléfono del cliente: un canal de la Cloud API se ofrece sólo si tiene la ventana abierta.
  const orderChannels = useWritableChannels(
    orderData?.store?.code,
    ownerWhatsapp || orderData?.user?.whatsapp,
  );
  const ownerContactId = (customerOwner as any)?.contact?.contactId;
  const ownerFullNameStr = ownerFullName;
  const ownerContactDetail = (orderData?.contactDetails as any[])?.find(
    (c: any) => c?.contactId === ownerContactId,
  );
  const ownerCompany =
    ownerContactDetail?.user?.company ??
    (customerOwner as any)?.company ??
    ownerUser?.company ??
    (customerOwner as any)?.contact?.company;

  const num = (v: any, d = 0) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : d;
  };
  const cents = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

  const paid = num(orderData?.totalPaid);
  const due = num(orderData?.grandTotal);
  const raw = cents(due - paid);

  const paymentStatus = norm(orderData?.payments?.[0]?.status);
  const mustZeroByPayment =
    paymentStatus === "invoiced" || paymentStatus === "canceled";

  const hasApprovedCreditMemo = (orderData?.creditMemos ?? []).some(
    (cm: any) => norm(cm?.status) === "approved",
  );

  // balance final
  const balance =
    mustZeroByPayment || hasApprovedCreditMemo
      ? 0
      : Math.abs(due) < 0.01
        ? 0
        : Math.max(0, due);

  // Número de factura del pedido. Se persiste en payment.metadata
  // (invoicePointOfSale / invoiceNumber); fallback a invoices[0] / invoice por
  // compatibilidad con pagos viejos. Mismo origen que usa el form de carga en
  // order-paymentItem, para que lo cargado se refleje también en este resumen.
  const primaryPayment = orderData?.payments?.[0];
  const invoicePointOfSale =
    primaryPayment?.metadata?.invoicePointOfSale ??
    primaryPayment?.invoices?.[0]?.pointOfSale ??
    primaryPayment?.invoice?.pointOfSale ??
    "";
  const invoiceNumber =
    primaryPayment?.metadata?.invoiceNumber ??
    primaryPayment?.invoices?.[0]?.number ??
    primaryPayment?.invoice?.number ??
    "";
  const hasInvoiceNumber =
    String(invoicePointOfSale).length > 0 ||
    String(invoiceNumber).length > 0;

  // Las entregas al final del flujo de la factura del pedido: las que se facturaron, aunque
  // después se hayan cancelado —en una cancelación total están todas—. **Sin las partes
  // canceladas:** una cancelación parcial separa lo cancelado en una entrega aparte con el
  // mismo nombre (`cancelationType: "partial"` y `sourceShipmentId`, la entrega de la que
  // salió), y la lista mostraba el nombre dos veces. Esa parte se ve en el flujo de su nota
  // de crédito (Diseño, 2026-10-19). No alcanza con `sourceShipmentId` solo: mover productos
  // también lo manda, y una entrega movida sí va.
  useEffect(() => {
    const esParteCancelada = (shipment: any) =>
      shipment?.metadata?.cancelationType === "partial" &&
      !!shipment?.metadata?.sourceShipmentId;
    setDeliveryNames(
      orderData?.shipments
        ?.filter((shipment: any) => !esParteCancelada(shipment))
        .map((shipment: any) => ({
          name: nombreDeEntrega(shipment?.metadata?.name),
          shipmentId: shipment?.shipmentId,
        })),
    );
  }, [orderData]);

  const API_KEY = VITE_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (
      orderData?.shippingAddress?.latitude &&
      orderData?.shippingAddress?.longitude
    ) {
      const imageUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${orderData?.shippingAddress?.latitude},${orderData?.shippingAddress?.longitude}&zoom=16&size=800x400&markers=color:red%7C${orderData?.shippingAddress?.latitude},${orderData?.shippingAddress?.longitude}&key=${API_KEY}`;
      setMapImage(imageUrl);
    }
  }, [
    orderData?.shippingAddress?.latitude,
    orderData?.shippingAddress?.longitude,
  ]);

  const HeaderCollapse = React.memo(
    ({
      icon,
      title,
      id,
      actions,
    }: {
      icon: React.ReactNode;
      title: string;
      id?: string;
      actions?: React.ReactNode;
    }) => (
      <Flex
        align="center"
        justify="space-between"
        gap={8}
        id={id}
        style={{ width: "100%" }}
      >
        <Flex align="center" gap={8}>
          {icon} <Text strong>{title}</Text>
        </Flex>
        {actions ? (
          <span onClick={(e) => e.stopPropagation()}>{actions}</span>
        ) : null}
      </Flex>
    ),
  );

  // console.log("-=ORDER DATA=-", orderData);

  useEffect(() => {
    if (orderData?.payments?.length) {
      const isInvoiced = orderData.payments.some(
        (p: any) => p.status === "invoiced",
      );
      setCanEnableDelivery(isInvoiced);
    }
  }, [orderData]);

  const collapseItems: CollapseProps["items"] = [
    {
      key: "1",
      label: (
        <HeaderCollapse
          icon={<InboxOutlined style={{ transform: "scale(1.2)" }} />}
          title={t("orders.label.section.ordersummary")}
        />
      ),
      children: (
        <OrderSumary
          sumaryItems={orderData?.items}
          shipmentItems={orderData?.shipments}
          interests={orderData?.instalmentsFinanciationPayment}
          additionalPayment={orderData?.additionalPayment}
          discountAmount={orderData?.discountAmount}
          discountDescription={orderData?.discountDescription}
        />
      ),
    },
    {
      key: "2",
      label: (
        <HeaderCollapse
          icon={<DollarOutlined style={{ fontSize: 18 }} />}
          title={t("orders.label.section.payment&invoice")}
        />
      ),
      children: (
        <OrderPayment
          delivery={deliveryNames}
          items={orderData?.payments}
          shipments={orderData?.shipments}
          invoice={orderData?.invoice}
          updateOrderData={refreshOrder}
          creditMemos={orderData?.creditMemos}
          billingAddress={orderData?.billingAddress}
          balance={balance}
          orderId={orderData?.orderId}
          orderStatus={orderData?.status}
          additionalPayment={orderData?.additionalPayment}
        />
      ),
    },
    (() => {
      const shipments = (orderData?.shipments ?? []) as any[];
      const pendingCount = shipments.filter(
        (s) => s?.status === "pending",
      ).length;
      const hasBulkActive = shipments.some(
        (s) =>
          s?.status === "pending_cancel" &&
          (s?.metadata as any)?.bulkCancelRequest?.groupId,
      );
      const canStartBulk =
        pendingCount > 0 && !hasBulkActive && bulkCancelMode === "off";
      return {
        key: "3",
        label: (
          <HeaderCollapse
            icon={<TruckOutlined style={{ fontSize: 18 }} />}
            title={t("Label.deliveries")}
            actions={
              <Dropdown
                trigger={["click"]}
                disabled={!canStartBulk}
                menu={{
                  items: [
                    {
                      key: "cancel-all-pending",
                      label: "Cancelar entregas pendientes",
                      disabled: !canStartBulk,
                      onClick: (e) => {
                        e.domEvent.stopPropagation();
                        setBulkCancelMode("select");
                      },
                    },
                  ],
                }}
              >
                <Button
                  type="link"
                  icon={<MoreOutlined />}
                  onClick={(e) => e.stopPropagation()}
                  disabled={!canStartBulk}
                />
              </Dropdown>
            }
          />
        ),
        children: (
          <OrderShipment
            items={orderData?.shipments}
            orderPayment={orderData?.payments?.[0]}
            products={orderData?.items}
            invoice={orderData?.invoice}
            canEnableDelivery={canEnableDelivery}
            updateOrderData={refreshOrder}
            interests={orderData?.instalmentsFinanciationPayment}
            additionalPayment={orderData?.additionalPayment}
            discountAmount={orderData?.discountAmount}
            orderId={orderData?.orderId}
            allShipments={orderData?.shipments}
            optimizations={orderData?.optimizations}
            orderItems={orderData?.items}
            bulkCancelMode={bulkCancelMode}
            onBulkCancelExit={() => setBulkCancelMode("off")}
            storeLink={orderData?.store?.link}
            creditMemos={orderData?.creditMemos}
          />
        ),
      };
    })(),
    {
      key: "4",
      label: (
        <HeaderCollapse
          icon={<StarOutlined style={{ fontSize: 18 }} />}
          title={t("orders.table.rating")}
        />
      ),
      children: <OrderRating />,
    },
  ];

  const IconComponent = React.useMemo(() => {
    const derived = deriveOrderShipmentStatus(orderData);

    if (derived === "canceled") return CubePlayCancel;
    if (derived === "complete") return CubePlayFinish;
    return CubePlay;
  }, [orderData]);

  // Label + color bajo el ícono de estado (como en Figma).
  const orderStatusLabel = React.useMemo(() => {
    const derived = deriveOrderShipmentStatus(orderData);
    if (derived === "canceled")
      return { label: t("orders.status.cancelled"), color: "#D9363E" };
    if (derived === "complete")
      return { label: t("orders.status.complete"), color: "#00B578" };
    return { label: t("orders.status.onGoing"), color: "#FF8F1F" };
  }, [orderData, t]);


  return (
    <Flex vertical>
      <Spin spinning={orderLoading}>
        <Flex
          align="center"
          justify="space-between"
          style={{ marginBottom: 20 }}
        >
          <Flex gap={8}>
            <ListButton
              onClick={() => handleBack()}
              hideText
              icon={<LeftOutlined />}
            />
            <Link
              href={`/contacts/${contacts?.[0]?.contactId}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Title style={{ marginBottom: 0, marginLeft: "10px", fontSize: 20 }} level={3}>
                {`#${orderData?.incrementId}`}{" "}
                <span style={{ color: token.colorLinkHover }}>
                  {ownerFullName ||
                    `${orderData?.customerFirstname ?? ""} ${orderData?.customerLastname ?? ""}`.trim()}
                </span>
              </Title>
            </Link>
          </Flex>
          <PermissionsLateral
            recordOrderId={recordOrderId}
            contactRecord={orderData}
            store={orderData?.store}
            initialParticipations={orderData?.orderParticipations}
            onChange={refreshOrder}
          />
        </Flex>

        <Row wrap justify="center" style={{ width: "100%" }}>
          <Col xs={24}>
        <Card size="small" style={{ width: "100%" }} styles={{ body: { padding: "14px 20px" } }}>
          <Flex align="center" justify="space-between" style={{ width: "100%" }} gap={12}>
            {/* Left: dos sub-columnas — contacto | orders+empresa */}
            <Flex gap={16} align="center" style={{ flex: 1 }}>
            <Flex vertical gap={6}>
              <Flex align="center" gap={8}>
                <WhatsAppOutlined style={{ fontSize: 14, color: "#15744b" }} />
                {(ownerWhatsapp || orderData?.user?.whatsapp) ? (
                  <ChannelPickerDropdown
                    channels={orderChannels.channels}
                    loading={orderChannels.loading}
                    failed={orderChannels.failed}
                    unavailable={orderChannels.unavailable}
                    onOpen={orderChannels.load}
                    placement="bottomLeft"
                    emptyText="No hay canales disponibles para este store"
                    onPick={(channelId) => {
                      const wa = ownerWhatsapp || orderData?.user?.whatsapp;
                      const email = displayEmail(ownerEmail || orderData?.user?.email);
                      const params = new URLSearchParams({ newMsg: "msg", channelId });
                      if (ownerContactId) params.set("contactId", String(ownerContactId));
                      if (ownerFullNameStr) params.set("contactName", ownerFullNameStr);
                      if (wa) params.set("whatsapp", wa);
                      if (email) params.set("email", email);
                      if (orderData?.store?.code) params.set("storeCode", orderData.store.code);
                      navigate(`/conversations?${params.toString()}`);
                    }}
                    onAddChannel={() => navigate(`/stores/channels/${orderData?.store?.code}`)}
                    extraItems={[{
                      key: "__wa_personal",
                      label: <Flex align="center" gap={8} style={{ padding: "4px 0", minWidth: 220 }}><WhatsAppOutlined style={{ fontSize: 16 }} /><span>WhatsApp Web personal</span></Flex>,
                      onClick: () => { window.open(`https://wa.me/${(ownerWhatsapp || orderData?.user?.whatsapp || "").replace(/[^\d]/g, "")}`, "_blank"); },
                    }]}
                  >
                    <Text strong style={{ color: token.colorLinkHover, fontSize: 12, cursor: "pointer" }}>
                      {ownerWhatsapp || orderData?.user?.whatsapp}
                    </Text>
                  </ChannelPickerDropdown>
                ) : (
                  <Text strong style={{ fontSize: 12 }}>-</Text>
                )}
              </Flex>
              <Flex align="center" gap={8}>
                <MailOutlined style={{ fontSize: 14, color: "#15744b" }} />
                {displayEmail(ownerEmail || orderData?.user?.email) ? (
                  <ChannelPickerDropdown
                    channels={orderChannels.channels}
                    loading={orderChannels.loading}
                    failed={orderChannels.failed}
                    unavailable={orderChannels.unavailable}
                    onOpen={orderChannels.load}
                    placement="bottomLeft"
                    emptyText="No hay canales disponibles para este store"
                    onPick={(channelId) => {
                      const wa = ownerWhatsapp || orderData?.user?.whatsapp;
                      const email = displayEmail(ownerEmail || orderData?.user?.email);
                      const params = new URLSearchParams({ newMsg: "msg", channelId });
                      if (ownerContactId) params.set("contactId", String(ownerContactId));
                      if (ownerFullNameStr) params.set("contactName", ownerFullNameStr);
                      if (wa) params.set("whatsapp", wa);
                      if (email) params.set("email", email);
                      if (orderData?.store?.code) params.set("storeCode", orderData.store.code);
                      navigate(`/conversations?${params.toString()}`);
                    }}
                    onAddChannel={() => navigate(`/stores/channels/${orderData?.store?.code}`)}
                    extraItems={[{
                      key: "__email_personal",
                      label: <Flex align="center" gap={8} style={{ padding: "4px 0", minWidth: 220 }}>{channelIcon("email")}<span>email personal</span></Flex>,
                      onClick: () => { window.location.href = `mailto:${displayEmail(ownerEmail || orderData?.user?.email)}`; },
                    }]}
                  >
                    <Text strong style={{ color: token.colorLinkHover, fontSize: 12, cursor: "pointer" }}>
                      {displayEmail(ownerEmail || orderData?.user?.email)}
                    </Text>
                  </ChannelPickerDropdown>
                ) : (
                  <Text strong style={{ fontSize: 12 }}>-</Text>
                )}
              </Flex>
            </Flex>

              {/* Sub-col 2: orders since + empresa */}
              <Flex vertical gap={6}>
                <Flex align="center" gap={6}>
                  <HistoryOutlined style={{ fontSize: 14, color: token.colorLinkHover }} />
                  <Text strong style={{ color: token.colorLinkHover, fontSize: 12 }}>
                    {orderData?.userOrdersCount ?? 0} orders since{" "}
                    {dayjs(orderData?.userFirstOrderAt || orderData?.createdAt).format("DD/MM/YYYY")}
                  </Text>
                </Flex>
                <Flex align="center" gap={6}>
                  <FlagOutlined style={{ fontSize: 14 }} />
                  <Text strong style={{ fontSize: 12 }}>{ownerCompany || "-"}</Text>
                </Flex>
              </Flex>
            </Flex>{/* end left section */}

            <Divider type="vertical" style={{ height: 40, margin: "0 4px" }} />

            <Flex gap={6} wrap="wrap" align="center" justify="flex-end" style={{ flex: 1 }}>
              <Spin spinning={orderLoading} percent="auto">
                {contactGroupsNames?.length === 0 && (
                  <Text italic type="secondary">
                    {t("contacts.table.noGroups")}
                  </Text>
                )}
                {contactGroupsNames?.map((name: string, index: number) => (
                  <Tag
                    key={index}
                    icon={<TeamOutlined style={{ fontSize: 14 }} />}
                    style={{
                      fontSize: 12,
                      height: 24,
                      padding: "0 4px",
                      borderRadius: 4,
                      background: "rgba(0,0,0,0.06)",
                      border: "1px solid rgba(0,0,0,0)",
                      color: "rgba(0,0,0,0.65)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    {name}
                  </Tag>
                ))}
              </Spin>
            </Flex>

          </Flex>
        </Card>
          </Col>
        </Row>
        {/* Las tres tarjetas, a 20px entre sí: lo mismo que las separa de la tarjeta de
            arriba y de las secciones de abajo (Diseño, 2026-10-19). */}
        <Row
          gutter={[20, 20]}
          wrap
          justify="center"
          // El gutter corre la fila 10px para cada lado: el ancho los suma, así los bordes
          // quedan alineados con la tarjeta de arriba y las secciones de abajo.
          style={{ width: "calc(100% + 20px)", marginTop: 20 }}
        >
          <Col xs={24} md={8} lg={8} style={{ display: "flex", flexDirection: "column" }}>
            <Card style={{ height: 242, overflow: "hidden" }} styles={{ body: { padding: 12 } }}>
              <Flex justify="space-between" align="center">
                <Flex vertical align="start" gap={2}>
                  <IconComponent width={56} height={56} />
                  <Text
                    strong
                    style={{ color: orderStatusLabel.color, fontSize: 13 }}
                  >
                    {orderStatusLabel.label}
                  </Text>
                </Flex>
                <Flex vertical align="end">
                  <Text strong>{t("products.import.table.date")}</Text>
                  <Title style={{ margin: 0, fontSize: 24 }} level={2}>
                    {dayjs(orderData?.createdAt).format("DD/MM/YYYY")}
                  </Title>
                  <Text>
                    <span
                      style={{
                        fontWeight: "bold",
                        marginRight: "5px",
                        color: token.colorTextSecondary,
                      }}
                    >
                      {t("products.show.tableUpdated")}
                    </span>
                    {dayjs(orderData?.updatedAt).format("DD/MM/YYYY - HH:mm")}
                    {" hs"}
                  </Text>
                </Flex>
              </Flex>
              <Divider style={{ margin: "16px 0" }} />
              <Flex vertical gap={8}>
                <Flex gap={6} align="center">
                  <PresupuestoIcon
                    style={{ transform: "scale(1.3)", marginLeft: 3 }}
                  />
                  <a
                    href={`/quotes/${orderData?.quote?.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (orderData?.quote?.id) {
                        push(`/quotes/${orderData.quote.id}`);
                      }
                    }}
                    style={{ color: "inherit", textDecoration: "none" }}
                  >
                    <Text strong style={{ color: token.colorLinkHover }}>
                      {`#${orderData?.quote?.id} ${orderData?.quote?.name}`}
                    </Text>
                  </a>
                </Flex>

                <Flex justify="space-between">
                  <Flex gap={6} align="center">
                    <ShopOutlined style={{ fontSize: 20 }} />
                    <Text style={{ color: token.colorTextSecondary }}>
                      {orderData?.store?.name}
                    </Text>
                  </Flex>

                  {storeOwner && (
                    <Text style={{ color: token.colorTextSecondary }}>
                      {`${storeOwner.contact?.user?.firstname} ${storeOwner.contact?.user?.lastname}`}
                    </Text>
                  )}
                </Flex>

                <Flex justify="space-between">
                  <Flex gap={6} align="center">
                    <SellerIcon
                      style={{
                        transform: "scale(1.5)",
                        marginLeft: 3,
                        marginRight: 3,
                      }}
                    />
                    <Text style={{ color: token.colorTextSecondary }}>
                      {orderData?.seller?.name}
                    </Text>
                  </Flex>
                  {sellerOwner && (
                    <Text style={{ color: token.colorTextSecondary }}>
                      {`${sellerOwner.contact?.user?.firstname} ${sellerOwner.contact?.user?.lastname}`}
                    </Text>
                  )}
                </Flex>
              </Flex>
            </Card>
          </Col>

          <Col xs={24} md={8} lg={8} style={{ display: "flex", flexDirection: "column" }}>
            <Card style={{ height: 242, overflow: "hidden" }} styles={{ body: { padding: 12 } }}>
              <Flex justify="space-between" align="center">
                <DollarOutlined style={{ fontSize: 56, color: "#15744B" }} />
                <Flex vertical align="end">
                  <Text strong>{t("orders.table.order")}</Text>
                  <Title style={{ margin: 0, fontSize: 24 }} level={2}>
                    {formatPrice(orderData?.grandTotal || 0)}
                  </Title>
                  <Text>
                    <span
                      style={{
                        fontWeight: "bold",
                        marginRight: "5px",
                        color: token.colorTextSecondary,
                      }}
                    >
                      {t("orders.label.balance")}
                    </span>

                    <Text>{`${formatPrice(balance)}`}</Text>
                  </Text>
                </Flex>
              </Flex>
              <Divider style={{ margin: "16px 0" }} />
              <Flex vertical gap={8}>
                <Flex vertical>
                  <Flex align="end" gap={6}>
                    <FileTextOutlined style={{ fontSize: 20 }} />
                    <Text strong>{t("orders.steps.orderInvoice")}</Text>

                    {hasInvoiceNumber ? (
                      <Text strong style={{ color: "#15744B" }}>
                        {`${invoicePointOfSale} - ${invoiceNumber}`}
                      </Text>
                    ) : balance > 0 ? (
                      <Text style={{ color: "#FF8F1F" }} italic>
                        {orderData?.payments?.[0]?.status ===
                          t("orders.tag.canceled")
                          ? ""
                          : t("orders-label.pending")}
                      </Text>
                    ) : (
                      <Text>-</Text>
                    )}
                  </Flex>
                  <Text style={{ color: token.colorTextSecondary }}>
                    {billingDisplayName(orderData?.billingAddress) || "-"}{" "}
                    {orderData?.billingAddress?.vatId || "-"}
                  </Text>
                  <Text type="secondary" strong>
                  {`${orderData?.billingAddress?.street}`}
                </Text>
                </Flex>

                {orderData?.payments?.length ? (
                  orderData.payments.map((payment: any, index: number) => (
                    <Flex key={index} align="center" gap={6}>
                      <BankOutlined style={{ fontSize: 20 }} />
                      <Text strong>
                        {payment.metadata?.method_title ||
                          payment.paymentMethod}
                      </Text>
                    </Flex>
                  ))
                ) : (
                  <Text>-</Text>
                )}
              </Flex>
            </Card>
          </Col>

          <Col xs={24} md={8} lg={8} style={{ display: "flex", flexDirection: "column" }}>
            <Card style={{ height: 242, overflow: "hidden" }} styles={{ body: { padding: 12 } }}>
              <Flex justify="space-between" align="center">
                <TruckOutlined
                  style={{ fontSize: 56, color: "rgba(0, 62, 179, 1)" }}
                />
                <Flex vertical align="end">
                  <Text strong>{t("orders.tag.delivered")}</Text>
                  <Title style={{ margin: 0, fontSize: 24 }} level={2}>
                    <ShipmentProgress shipments={orderData?.shipments || []} />
                  </Title>
                  <Text>
                    <span
                      style={{
                        fontWeight: "bold",
                        marginRight: "5px",
                        color: token.colorTextSecondary,
                      }}
                    >
                      {t("orders.label.nextdelivery")}
                    </span>
                    {orderData?.invoice?.customId || "-"}
                  </Text>
                </Flex>
              </Flex>
              <Divider style={{ margin: "16px 0" }} />
              <Card
                className="map-container"
                style={{
                  backgroundImage: `
    linear-gradient(90deg,rgba(250, 250, 250, .9) 25%, rgba(100, 100, 100, .05) 100%),
    url(${mapImage ?? mapImagePng})
  `,

                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                }}
              >
                <Flex vertical gap={8}>
                  <Flex
                    align="start"
                    justify="center"
                    vertical
                    style={{
                      width: "fit-content",
                      padding: "8px 0 0 8px",
                    }}
                  >
                    <Flex gap={6}>
                      <EnvironmentOutlined style={{ fontSize: 20 }} />
                      <Text style={{ color: token.colorTextSecondary }}>
                        {orderData?.shippingAddress?.street}
                      </Text>
                    </Flex>
                    <Text
                      style={{
                        color: token.colorTextSecondary,
                        marginLeft: 26,
                      }}
                    >
                      {`${orderData?.shippingAddress?.city} ${orderData?.shippingAddress?.region}`}
                    </Text>
                  </Flex>

                  <LateralModal
                    title={t("orders.title.editdeliveryaddress")}
                    width="50%"
                    buttonSize="small"
                    buttonShape="default"
                    buttonPosition="end"
                    iconSvg={<EditOutlined />}
                  >
                    <DeliveryAddress
                      address={orderData?.shippingAddress}
                      updateOrderData={refreshOrder}
                      orderId={recordOrderId}
                    />
                  </LateralModal>
                </Flex>
              </Card>
            </Card>
          </Col>
        </Row>

        <Row
          wrap
          justify="center"
          style={{ width: "100%", marginTop: 20 }}
        >
          <Col xs={24} md={24} lg={24}>
            <Collapse
              className="order-sections-collapse"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.02)",
                borderColor: "rgba(217, 217, 217, 1)",
              }}
              items={collapseItems}
              activeKey={activeCollapseKey}
              onChange={(key) => {
                setActiveCollapseKey(Array.isArray(key) ? key : [key]);
              }}
            />
          </Col>
        </Row>

        {/* ===== HISTORIAL ===== */}
        <Card
          className="history-card order-history-card"
          styles={{
            header: { height: 40, minHeight: 40, padding: "0 16px" },
            body: { padding: 0, overflow: "hidden", display: "flex", flexDirection: "column" },
          }}
          title={
            <Flex align="center" gap={24} style={{ width: "100%" }}>
              <Flex gap={4} align="center" style={{ flexShrink: 0 }}>
                <HistoryOutlined style={{ fontSize: 16 }} />
                <Text style={{ fontSize: 14, fontWeight: 600 }}>Historial</Text>
              </Flex>
              <Flex flex={1} gap={16} align="center">
                <Input.Search
                  size="small"
                  allowClear
                  placeholder="Buscar en el historial..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  style={{ flex: 1 }}
                />
              </Flex>
            </Flex>
          }
          style={{ marginTop: 20 }}
        >
          {orderData?.orderId && (
            <div style={{ padding: 12 }}>
              <HistorySection module="order" entityId={String(orderData.orderId)} externalQ={historyQ} />
            </div>
          )}
        </Card>
      </Spin>
    </Flex>
  );
};
